import TagManager from 'react-gtm-module';
import { DefaultSeo } from 'next-seo';
import SEO from 'next-seo.config';
import { useEffect, useState } from 'react';
import 'styles/globals.scss';
import { getCookie } from 'cookies-next';
import { wrapper } from '../store';
import NextNProgress from 'nextjs-progressbar';
import AppLayout from 'layouts/AppLayout';
import { useSelector, useStore } from 'react-redux';
import auth from '../utils/auth';
import { authSelector, authSuccess, logout } from '../store/auth';
import { handleResetData } from '../store/company';
import {
    AUTH_TOKEN_COOKIE,
    REFRESH_TOKEN_COOKIE,
} from '../data/globalConstants';
import { useRouter } from 'next/router';

const MyApp = ({ Component, pageProps }) => {
    const router = useRouter();
    const isEmbedded = router.pathname.startsWith('/embed');

    useEffect(() => {
        // GTM has no hostname filter, so staging and local traffic landed in the prod GA4 stream.
        if (process.env.ENV !== 'prod') {
            return;
        }
        TagManager.initialize({
            gtmId: 'GTM-W68FPWM',
        });
    }, []);

    const store = useStore();
    const { isAuthenticated, id } = useSelector(authSelector);

    const [isAuthFinished, setIsAuthFinished] = useState(false);
    useEffect(() => {
        // Initializes auth logic, including interceptors when the app is first rendered
        (async () => {
            await auth(store);
            // utils/auth only marks the store authenticated on its server path,
            // and a cached page hydrates with no auth at all, so the client pass
            // has to do it. auth() deletes both cookies on any failure, so both
            // surviving is the same condition the server path ends on.
            const token = getCookie(AUTH_TOKEN_COOKIE);
            if (token && getCookie(REFRESH_TOKEN_COOKIE)) {
                store.dispatch(authSuccess(token));
            }
            setIsAuthFinished(true);
        })();
    }, []);

    // Signing in leaves the store with a token but no user: the login form only
    // dispatches authenticate, and the page it redirects to is cached, so
    // nothing fetches /users/me for the new session until the next full load.
    useEffect(() => {
        if (isAuthenticated && !id) {
            auth(store);
        }
    }, [isAuthenticated, id]);

    // A session can die while the tab stays open, and only the mount pass above
    // would notice - every page that would catch it is cached. The store goes on
    // saying "signed in" off an unvalidated JWT, so a server-rendered page sends
    // the signed-out request to login or register, those read the store, believe
    // it, and replace straight back to the profile. No reload, no error, nothing
    // resets - so the click looks like it did nothing, every time. utils/auth
    // treats a missing refresh cookie as no session; match it here.
    useEffect(() => {
        const reconcile = () => {
            const { auth: session } = store.getState();
            if (!session.isAuthenticated) return;
            if (getCookie(REFRESH_TOKEN_COOKIE)) return;
            store.dispatch(logout());
            store.dispatch(handleResetData());
        };
        router.events.on('routeChangeStart', reconcile);
        return () => router.events.off('routeChangeStart', reconcile);
    }, [router.events, store]);

    // Rendered before the auth pass resolves on purpose: cached pages are built
    // signed-out, so this first render is what their HTML hydrates against, and
    // every component here reads the auth slice and re-renders once it lands.
    if (isEmbedded) {
        return <Component {...pageProps} isAuthFinished={isAuthFinished} />;
    }

    return (
        <>
            <DefaultSeo {...SEO} />
            <NextNProgress color="var(--color-blue)" />
            <AppLayout>
                <Component {...pageProps} isAuthFinished={isAuthFinished} />
            </AppLayout>
        </>
    );
};

export default wrapper.withRedux(MyApp);
